import { useEffect, useRef, useState } from 'react';

/**
 * Tiny imperative SVG animation engine for the landing page.
 *
 * A scene is a pure function of time `frame(t)` returning circles, lines,
 * paths, rings and labels keyed by id. The engine keeps one SVG element per
 * id and only rewrites attributes each frame, so 100+ animated shapes stay
 * cheap (no React re-render per frame). Scenes loop every `duration` seconds,
 * pause while off-screen and honour prefers-reduced-motion (one still frame).
 */

export interface SNode {
  id: string;
  x: number;
  y: number;
  r: number;
  fill: string;
  o?: number;
  /** Dashed outline, empty fill — a funding wallet. */
  ring?: boolean;
  /** 0..1 white outline flash (scan highlight). */
  hl?: number;
  stroke?: string;
  /** 0..1 soft blue bloom behind the bubble. */
  glow?: number;
}
export interface SLink {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** 0..1 how much of the line is drawn (from x1,y1). */
  draw?: number;
  color: string;
  o?: number;
  w?: number;
  dash?: string;
}
export interface SPath {
  id: string;
  d: string;
  draw?: number;
  color: string;
  o?: number;
  w?: number;
  dash?: string;
}
export interface SRing {
  id: string;
  cx: number;
  cy: number;
  r: number;
  color: string;
  o?: number;
  w?: number;
  dash?: string;
}
export interface SText {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  o?: number;
  size?: number;
  weight?: number;
  anchor?: 'start' | 'middle' | 'end';
  mono?: boolean;
}
export interface Frame {
  paths?: SPath[];
  links?: SLink[];
  rings?: SRing[];
  nodes: SNode[];
  texts?: SText[];
}
export interface Scene {
  w: number;
  h: number;
  duration: number;
  frame: (t: number) => Frame;
  /** Story beat at time t (drives captions / verdicts in React). */
  phase: (t: number) => number;
  /** Time shown when motion is reduced. */
  still: number;
}

const NS = 'http://www.w3.org/2000/svg';
type Layer = 'glows' | 'paths' | 'links' | 'rings' | 'nodes' | 'texts';

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export function SceneCanvas({
  scene,
  playing,
  onPhase,
  jump,
  label,
}: {
  scene: Scene;
  playing: boolean;
  onPhase?: (phase: number) => void;
  /** Change `n` to restart the scene at time `t`. */
  jump?: { t: number; n: number };
  label: string;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const layers = useRef<Record<Layer, SVGGElement | null>>({ glows: null, paths: null, links: null, rings: null, nodes: null, texts: null });
  const glowId = useRef(`lpglow${Math.random().toString(36).slice(2, 8)}`).current;
  const els = useRef(new Map<string, SVGElement>());
  const tRef = useRef(0);
  const phaseRef = useRef(-1);
  const onPhaseRef = useRef(onPhase);
  onPhaseRef.current = onPhase;
  /** On-screen px per scene unit; keeps labels readable when the stage is small (phones). */
  const scaleRef = useRef(1);

  useEffect(() => {
    const el = svgRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      scaleRef.current = (el.clientWidth || scene.w) / scene.w;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [scene]);

  // A new scene starts from scratch.
  useEffect(() => {
    els.current.forEach((el) => el.remove());
    els.current.clear();
    tRef.current = 0;
    phaseRef.current = -1;
  }, [scene]);

  useEffect(() => {
    if (jump) tRef.current = jump.t;
  }, [jump?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const draw = (t: number) => {
      const f = scene.frame(t);
      const seen = new Set<string>();
      const get = (layer: Layer, id: string, tag: string) => {
        const key = `${layer}:${id}`;
        seen.add(key);
        let el = els.current.get(key);
        if (!el) {
          el = document.createElementNS(NS, tag) as SVGElement;
          layers.current[layer]?.appendChild(el);
          els.current.set(key, el);
        }
        return el;
      };
      const set = (el: SVGElement, a: Record<string, string | number>) => {
        for (const k in a) el.setAttribute(k, String(a[k]));
      };

      for (const p of f.paths ?? []) {
        const el = get('paths', p.id, 'path');
        set(el, {
          d: p.d,
          fill: 'none',
          stroke: p.color,
          'stroke-width': p.w ?? 2,
          'stroke-linecap': 'round',
          'stroke-linejoin': 'round',
          pathLength: 1,
          'stroke-dasharray': p.dash ?? `${Math.max(0, Math.min(1, p.draw ?? 1))} 2`,
          opacity: p.o ?? 1,
        });
      }
      for (const l of f.links ?? []) {
        const el = get('links', l.id, 'line');
        const d = Math.max(0, Math.min(1, l.draw ?? 1));
        set(el, {
          x1: l.x1,
          y1: l.y1,
          x2: l.x1 + (l.x2 - l.x1) * d,
          y2: l.y1 + (l.y2 - l.y1) * d,
          stroke: l.color,
          'stroke-width': l.w ?? 1.4,
          'stroke-linecap': 'round',
          'stroke-dasharray': l.dash ?? 'none',
          opacity: d <= 0 ? 0 : (l.o ?? 1),
        });
      }
      for (const r of f.rings ?? []) {
        const el = get('rings', r.id, 'circle');
        set(el, {
          cx: r.cx,
          cy: r.cy,
          r: Math.max(0, r.r),
          fill: 'none',
          stroke: r.color,
          'stroke-width': r.w ?? 1.5,
          'stroke-dasharray': r.dash ?? 'none',
          opacity: r.o ?? 1,
        });
      }
      for (const n of f.nodes) {
        if (n.glow && n.glow > 0.01) {
          const g = get('glows', n.id, 'circle');
          set(g, { cx: n.x, cy: n.y, r: Math.max(0, n.r * 1.15), fill: n.ring ? (n.stroke ?? n.fill) : n.fill, opacity: (n.glow * (n.o ?? 1) * 0.85).toFixed(3) });
        }
        const el = get('nodes', n.id, 'circle');
        const hl = n.hl ?? 0;
        set(el, {
          cx: n.x,
          cy: n.y,
          r: Math.max(0, n.r),
          fill: n.ring ? '#060709' : n.fill,
          stroke: n.ring ? n.stroke ?? n.fill : hl > 0.01 ? `rgba(255,255,255,${(hl * 0.9).toFixed(3)})` : 'none',
          'stroke-width': n.ring ? 1.6 : 2,
          'stroke-dasharray': n.ring ? '3.2 2.6' : 'none',
          opacity: n.o ?? 1,
        });
      }
      for (const tx of f.texts ?? []) {
        const el = get('texts', tx.id, 'text');
        const size = tx.size ?? 12;
        set(el, {
          x: tx.x,
          y: tx.y,
          fill: tx.color,
          'font-size': Math.max(size, 10.5 / scaleRef.current),
          'font-weight': tx.weight ?? 600,
          'text-anchor': tx.anchor ?? 'start',
          class: tx.mono ? 'mono' : '',
          opacity: tx.o ?? 1,
        });
        if (el.textContent !== tx.text) el.textContent = tx.text;
      }
      // Anything the scene didn't draw this frame is hidden, not removed.
      els.current.forEach((el, key) => {
        if (!seen.has(key)) el.setAttribute('opacity', '0');
      });

      const ph = scene.phase(t);
      if (ph !== phaseRef.current) {
        phaseRef.current = ph;
        onPhaseRef.current?.(ph);
      }
    };

    if (reducedMotion()) {
      draw(scene.still);
      return;
    }
    if (!playing) {
      draw(tRef.current);
      return;
    }
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      // Clamp the step so a backgrounded tab doesn't skip whole story beats.
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      tRef.current = (tRef.current + dt) % scene.duration;
      draw(tRef.current);
      raf = requestAnimationFrame(tick);
    };
    draw(tRef.current);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scene, playing, jump?.n]);

  return (
    <svg ref={svgRef} className="lp-scene" viewBox={`0 0 ${scene.w} ${scene.h}`} role="img" aria-label={label}>
      <defs>
        <filter id={glowId} x="-100%" y="-100%" width="300%" height="300%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
      </defs>
      <g ref={(g) => void (layers.current.glows = g)} filter={`url(#${glowId})`} />
      <g ref={(g) => void (layers.current.paths = g)} />
      <g ref={(g) => void (layers.current.links = g)} />
      <g ref={(g) => void (layers.current.rings = g)} />
      <g ref={(g) => void (layers.current.nodes = g)} />
      <g ref={(g) => void (layers.current.texts = g)} />
    </svg>
  );
}

/** True once (or while) the element is on screen. */
export function useInView<T extends Element>(opts: { once?: boolean; margin?: string } = {}) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e!.isIntersecting) {
          setInView(true);
          if (opts.once) io.disconnect();
        } else if (!opts.once) setInView(false);
      },
      { rootMargin: opts.margin ?? '0px 0px -12% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, inView] as const;
}

